import type { ReactNode } from 'react';
import { clsx } from 'clsx';
import styles from './Hero.module.css';

export interface HeroProps {
  /**
   * Small text above the title (e.g., "New Feature")
   */
  eyebrow?: string;

  /**
   * Main heading text
   */
  title: string;

  /**
   * Description text
   */
  description: string;

  /**
   * Primary call-to-action button
   */
  primaryAction?: ReactNode;

  /**
   * Secondary call-to-action button
   */
  secondaryAction?: ReactNode;

  /**
   * Additional content (e.g., StatBar, badges)
   */
  children?: ReactNode;

  /**
   * Background image URL
   */
  background?: string;

  /**
   * Text alignment
   * @default 'center'
   */
  align?: 'left' | 'center' | 'right';

  /**
   * Hero section size
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Additional CSS classes
   */
  className?: string;
}

/**
 * Hero Component
 *
 * A prominent section typically used at the top of a page to showcase
 * the main message and call-to-action.
 *
 * @example
 * ```tsx
 * <Hero
 *   eyebrow="Introducing"
 *   title="Automated Document Intelligence"
 *   description="Transform unstructured documents into ERP-ready data"
 *   primaryAction={<Button>Get Started</Button>}
 *   secondaryAction={<Button variant="outline">Learn More</Button>}
 * />
 * ```
 */
export const Hero = ({
  eyebrow,
  title,
  description,
  primaryAction,
  secondaryAction,
  children,
  background,
  align = 'center',
  size = 'md',
  className
}: HeroProps) => {
  const sectionStyle = background
    ? {
        backgroundImage: `url(${background})`
      }
    : undefined;

  return (
    <section
      className={clsx(
        styles.hero,
        background && styles.hasBackground,
        styles[`align${align.charAt(0).toUpperCase()}${align.slice(1)}`],
        styles[`size-${size}`],
        className
      )}
      style={sectionStyle}
    >
      <div className={styles.container}>
        {eyebrow && <div className={styles.eyebrow}>{eyebrow}</div>}

        <h1 className={styles.title}>{title}</h1>

        <p className={styles.description}>{description}</p>

        {(primaryAction || secondaryAction) && (
          <div className={styles.actions}>
            {primaryAction}
            {secondaryAction}
          </div>
        )}

        {children && <div className={styles.children}>{children}</div>}
      </div>
    </section>
  );
};
